import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import api from '../utils/api';
import ContractAgreement from '../components/contracts/ContractAgreement';
import { CONTRACT_VERSION } from '../constants/contract';
import { useUser } from '../contexts/UserContext';
import { lightTheme } from '../theme';

const PrivateRoute: React.FC = () => {
  const { user: contextUser, setUser } = useUser();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresContractAcceptance, setRequiresContractAcceptance] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState<{ email?: string; id?: number | null } | null>(null);
  const [checkingContract, setCheckingContract] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const checkAuthStatus = async () => {
      try {
        console.log(`Checking authentication with API URL: ${api.defaults.baseURL}`);

        // Always check authentication with backend via cookie
        const response = await api.get('/api/users/me');

        if (response.data && isMounted) {
          console.log('User authenticated successfully:', response.data.email);

          const userPayload = {
            email: response.data.Email,
            Role: response.data.Role,
            role: response.data.Role,
            id: response.data.UserID,
            companyID: response.data.CompanyID,
            distributorID: response.data.DistributorID || null,
            organizationID: response.data.OrganizationID || null,
            clinicID: response.data.ClinicID || null,
            Name: response.data.Name || undefined,
            Username: response.data.Username || undefined,
          };

          setUser(userPayload);
          setIsAuthenticated(true);
          setError(null);
          setAuthenticatedUser({ email: response.data.Email, id: response.data.UserID });

          // Check contract status from backend
          const contractSigned = response.data.ContractSigned === true || response.data.ContractSigned === 1;
          const contractVersion = response.data.ContractVersion;
          const hasCurrentVersion = contractVersion === CONTRACT_VERSION;

          // Require signature if not signed OR if signed version doesn't match current version
          const requiresSignature = !contractSigned || !hasCurrentVersion;
          
          console.log('Contract status check:', {
            contractSigned,
            contractVersion,
            currentVersion: CONTRACT_VERSION,
            requiresSignature,
          });

          setRequiresContractAcceptance(requiresSignature);
          setCheckingContract(false);
        }
      } catch (err: any) {
        if (isMounted) {
          console.error('Authentication check failed:', err);
          setUser(null);
          setIsAuthenticated(false);
          setAuthenticatedUser(null);
          setRequiresContractAcceptance(false);
          setCheckingContract(false);

          if (err.code === 'ERR_NETWORK') {
            setError('Unable to connect to the server. Please refresh or try again later.');
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Check if we have user data in context for initial state
    if (contextUser) {
      setIsLoading(true);
    } else {
      setIsAuthenticated(false);
      setCheckingContract(false);
    }

    // Always check with the backend to verify authentication
    checkAuthStatus();

    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading || checkingContract) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1">Verifying your session...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3 }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>Connection Error</Typography>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>{error}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Retry Connection
        </Button>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const handleContractAcceptance = async () => {
    // Refetch user data to get updated contract status
    try {
      const response = await api.get('/api/users/me');
      
      if (response.data) {
        const contractSigned = response.data.ContractSigned === true || response.data.ContractSigned === 1;
        const contractVersion = response.data.ContractVersion;
        const hasCurrentVersion = contractVersion === CONTRACT_VERSION;
        
        if (contractSigned && hasCurrentVersion) {
          setRequiresContractAcceptance(false);
          console.log('✅ Contract acceptance verified, allowing access to platform');
        } else {
          console.warn('⚠️ Contract status not updated correctly. Rechecking...');
          // Force recheck
          window.location.reload();
        }
      }
    } catch (err) {
      console.error('Error verifying contract acceptance:', err);
      // Still allow access if verification fails (prevents blocking users)
      setRequiresContractAcceptance(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/logout', {}, { withCredentials: true });
    } catch (logoutError) {
      console.warn('Logout request failed while exiting contract agreement.', logoutError);
    } finally {
      setUser(null);
      navigate('/login', { replace: true });
    }
  };

  if (requiresContractAcceptance) {
    return (
      <MuiThemeProvider theme={lightTheme}>
        <ContractAgreement
          onAccept={handleContractAcceptance}
          onLogout={handleLogout}
          userEmail={authenticatedUser?.email ?? undefined}
        />
      </MuiThemeProvider>
    );
  }

  return <Outlet />;
};

export default PrivateRoute;
